import axios from 'axios';
import { User, Role } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface CreateUserDto {
  email: string;
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  roleId: string;
  isActive: boolean;
}

export interface UpdateUserDto {
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  roleId?: string;
  isActive?: boolean;
}

export const userService = {
  async getUsers(page: number = 1, limit: number = 10, search?: string): Promise<{ users: User[], total: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) params.append('search', search);

    const response = await api.get(`/users?${params}`);
    return response.data;
  },

  async getUserById(id: string): Promise<User> {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  async createUser(data: CreateUserDto): Promise<User> {
    const response = await api.post('/users', data);
    return response.data;
  },

  async updateUser(id: string, data: UpdateUserDto): Promise<User> {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  async resetPassword(id: string): Promise<{ temporaryPassword: string }> {
    const response = await api.post(`/users/${id}/reset-password`);
    return response.data;
  },

  async getRoles(): Promise<Role[]> {
    const response = await api.get('/roles');
    return response.data.data.map((role: any) => ({
      ...role,
      permissions: role.Permissions || role.permissions || []
    }));
  },

  async getAllRoles(): Promise<Role[]> {
    const response = await api.get('/roles/all/roles');
    return response.data.data.map((role: any) => ({
      ...role,
      permissions: role.Permissions || role.permissions || []
    }));
  },

  async createRole(data: { name: string; description: string; permissions: string[] }): Promise<Role> {
    const response = await api.post('/roles', data);
    return response.data.data;
  },

  async updateRole(id: string, data: { name?: string; description?: string; permissions?: string[] }): Promise<Role> {
    const response = await api.put(`/roles/${id}`, data);
    return response.data.data;
  },

  async deleteRole(id: string): Promise<void> {
    await api.delete(`/roles/${id}`);
  },

  async getPermissions(): Promise<string[]> {
    const response = await api.get('/permissions');
    return response.data;
  },
};